import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Inbox,
  SlidersHorizontal,
  X
} from "lucide-react";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { EDSButton } from "../../../../../components/eds/EDSButton";
import { Checkbox } from "../../../../../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../../../../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../../../components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle
} from "../../../../../components/ui/sheet";
import { Skeleton } from "../../../../../components/ui/skeleton";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import {
  Enquiry,
  EnquiryStatus,
  FilterOptions
} from "../../../../../types/enquiries";
import type { BulkDeleteReason } from "../../../api";
import { BookingLabel, User } from "../../../../../types/VenueGroup";
import { BulkActionBar } from "../BulkActionBar";
import type { BulkUpdateFields } from "../BulkActionBar/BulkActionBar";
import { EnquiryCard } from "../EnquiryCard";
import { AssignToModal } from "../EnquiryCard/AssignToModal";
import { STATUS_CONFIG } from "../constants";
import { useBulkSelection } from "../../../useBulkSelection";

export interface EnquiriesMainProps {
  enquiries: Enquiry[];
  users: User[];
  loading: boolean;
  error: string | null;
  filters: FilterOptions;
  total: number;
  offset: number;
  limit: number;
  recentlyUpdatedIds: string[];
  filteredTotalHeads: number;
  currentUserId?: string;
  isInternal?: boolean;
  userCanReject?: boolean;
  userCanDelete?: boolean;
  filterDrawerContent?: React.ReactNode;
  takeEnquiry: (id: string) => void;
  updateStatus: (id: string, status: EnquiryStatus) => void;
  assignEnquiry: (id: string, userId: string | undefined) => void;
  clearNotifications: (selectedIds: Set<string>) => Promise<void>;
  bulkRejectEnquiries: (selectedIds: Set<string>) => Promise<void>;
  bulkDeleteEnquiries: (
    selectedIds: Set<string>,
    reason?: BulkDeleteReason
  ) => Promise<void>;
  bulkAssignEnquiries: (
    selectedIds: Set<string>,
    userId: string | undefined
  ) => Promise<void>;
  bulkApplyUpdates: (
    selectedIds: Set<string>,
    updates: BulkUpdateFields
  ) => Promise<void>;
  bulkActionError?: string | null;
  clearBulkActionError?: () => void;
  isBulkApplying?: boolean;
  bookingLabels?: BookingLabel[];
  refetch: () => void;
  onPaginate: (offset: number) => void;
  onStatusFilterClick: (status: EnquiryStatus) => void;
  onClearFilters: () => void;
}

export const EnquiriesMain: React.FC<EnquiriesMainProps> = ({
  enquiries,
  users,
  loading,
  error,
  filters,
  total,
  offset,
  limit,
  recentlyUpdatedIds,
  filteredTotalHeads,
  currentUserId,
  isInternal = false,
  userCanReject = false,
  userCanDelete = false,
  filterDrawerContent,
  takeEnquiry,
  updateStatus,
  assignEnquiry,
  clearNotifications,
  bulkRejectEnquiries,
  bulkDeleteEnquiries,
  bulkAssignEnquiries,
  bulkApplyUpdates,
  bulkActionError = null,
  clearBulkActionError,
  isBulkApplying = false,
  bookingLabels = [],
  refetch,
  onPaginate,
  onStatusFilterClick,
  onClearFilters
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setDiaryContext } = useDiaryContext();
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);
  const [isRejectDialogOpen, setIsRejectDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isBulkAssignModalOpen, setIsBulkAssignModalOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState<BulkDeleteReason | "">("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const {
    selectedIds,
    isSelected,
    toggle,
    selectAll,
    deselectAll,
    selectedCount,
    isAllSelected
  } = useBulkSelection();

  const searchRef = useRef(location.search);
  searchRef.current = location.search;

  const enquiryIds = useMemo(() => enquiries.map((e) => e._id), [enquiries]);

  const allSelected = isAllSelected(enquiryIds);

  useEffect(() => {
    deselectAll();
  }, [offset, deselectAll]);

  const handleSelectAllChange = useCallback(
    (checked: boolean | "indeterminate") => {
      if (checked === true) {
        selectAll(enquiryIds);
      } else {
        deselectAll();
      }
    },
    [enquiryIds, selectAll, deselectAll]
  );

  const handleToggleSelect = useCallback(
    (id: string) => {
      toggle(id);
    },
    [toggle]
  );

  const handleBulkClearNotifications = useCallback(async () => {
    try {
      await clearNotifications(selectedIds);
      deselectAll();
    } catch {
      // Selection preserved on failure; error shown via notification system
    }
  }, [clearNotifications, selectedIds, deselectAll]);

  const handleBulkRejectClick = useCallback(() => {
    setIsRejectDialogOpen(true);
  }, []);

  const handleBulkRejectConfirm = useCallback(async () => {
    setIsRejecting(true);
    try {
      await bulkRejectEnquiries(selectedIds);
      deselectAll();
      setIsRejectDialogOpen(false);
    } catch {
      // Selection preserved on failure; error shown via bulkActionError
    } finally {
      setIsRejecting(false);
    }
  }, [selectedIds, bulkRejectEnquiries, deselectAll]);

  const handleBulkRejectCancel = useCallback(() => {
    setIsRejectDialogOpen(false);
  }, []);

  const handleBulkAssignClick = useCallback(() => {
    setIsBulkAssignModalOpen(true);
  }, []);

  const handleBulkAssignSave = useCallback(
    async (userId: string | undefined) => {
      await bulkAssignEnquiries(selectedIds, userId);
      deselectAll();
      setIsBulkAssignModalOpen(false);
    },
    [selectedIds, bulkAssignEnquiries, deselectAll]
  );

  const handleBulkAssignCancel = useCallback(() => {
    setIsBulkAssignModalOpen(false);
  }, []);

  const handleBulkApplyUpdates = useCallback(
    async (updates: BulkUpdateFields) => {
      try {
        await bulkApplyUpdates(selectedIds, updates);
        deselectAll();
      } catch {
        // Selection preserved on failure; error shown via bulkActionError
      }
    },
    [selectedIds, bulkApplyUpdates, deselectAll]
  );

  const handleBulkDeleteClick = useCallback(() => {
    setIsDeleteDialogOpen(true);
  }, []);

  const handleBulkDeleteConfirm = useCallback(async () => {
    setIsDeleting(true);
    try {
      const reason: BulkDeleteReason = deleteReason || "other";
      await bulkDeleteEnquiries(selectedIds, reason);
      deselectAll();
      setIsDeleteDialogOpen(false);
      setDeleteReason("");
    } catch {
      // Error already handled by the API layer
    } finally {
      setIsDeleting(false);
    }
  }, [selectedIds, deleteReason, bulkDeleteEnquiries, deselectAll]);

  const handleBulkDeleteCancel = useCallback(() => {
    setIsDeleteDialogOpen(false);
    setDeleteReason("");
  }, []);

  const handleViewEnquiry = useCallback(
    (enquiry: Enquiry) => {
      setDiaryContext({
        selectedEnquiry: enquiry,
        enquiriesReturnSearch: searchRef.current,
        openedFromEnquiry: true
      });
      navigate(`/bookings/${enquiry._id}`);
    },
    [navigate, setDiaryContext]
  );

  const handlePrevPage = useCallback(() => {
    if (offset > 0) {
      onPaginate(Math.max(0, offset - limit));
    }
  }, [offset, limit, onPaginate]);

  const handleNextPage = useCallback(() => {
    if (offset + limit < total) {
      onPaginate(offset + limit);
    }
  }, [offset, limit, total, onPaginate]);

  const startItem = total > 0 ? offset + 1 : 0;
  const endItem = Math.min(offset + limit, total);

  const activeFilterCount = Object.values(filters).filter(
    (v) => v !== undefined && (Array.isArray(v) ? v.length > 0 : true)
  ).length;

  const renderEnquiriesContent = (): React.ReactNode => {
    if (loading) {
      return (
        <div aria-label="Loading enquiries">
          {Array.from({ length: limit }, (_, i) => (
            <div
              key={`skeleton-${i}`}
              className="relative flex items-center gap-3 p-3 pl-14 pr-16 border-b border-border bg-card"
            >
              <Skeleton className="absolute left-3 h-8 w-8 rounded-full" />
              <div className="flex-1 min-w-0 space-y-2">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-4 w-32" />
                  <Skeleton className="h-5 w-16 rounded-full" />
                </div>
                <div className="flex items-center gap-3">
                  <Skeleton className="h-3.5 w-10" />
                  <Skeleton className="h-3.5 w-24" />
                  <Skeleton className="h-3.5 w-14" />
                  <Skeleton className="h-3.5 w-20" />
                </div>
              </div>
            </div>
          ))}
        </div>
      );
    }

    if (error) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 p-8 text-center">
          <AlertCircle size={48} className="text-destructive" />
          <h3 className="text-lg font-semibold">Failed to load enquiries</h3>
          <p className="text-muted-foreground max-w-md">{error}</p>
          <EDSButton onClick={() => refetch()}>Try again</EDSButton>
        </div>
      );
    }

    if (enquiries.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-full gap-4 p-8 text-center">
          <Inbox size={64} className="text-muted-foreground/50" />
          <h3 className="text-lg font-semibold">No enquiries found</h3>
          <p className="text-muted-foreground max-w-md">
            {activeFilterCount > 0
              ? "Try adjusting your filters to see more results."
              : "There are no enquiries to display at the moment."}
          </p>
        </div>
      );
    }

    return (
      <div>
        {enquiries.map((enquiry) => (
          <EnquiryCard
            key={enquiry._id}
            enquiry={enquiry}
            users={users}
            isRecentlyUpdated={recentlyUpdatedIds.includes(enquiry._id)}
            isSelected={isSelected(enquiry._id)}
            onToggleSelect={handleToggleSelect}
            onTake={takeEnquiry}
            onStatusChange={updateStatus}
            onAssign={assignEnquiry}
            onView={handleViewEnquiry}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full max-h-[calc(100dvh-60px-var(--workspace-tab-adjustment,0px)-var(--safe-area-inset-top,0px))] overflow-hidden">
      {selectedCount > 0 && (
        <BulkActionBar
          selectedCount={selectedCount}
          isInternal={isInternal}
          userCanReject={userCanReject}
          userCanDelete={userCanDelete}
          bookingLabels={bookingLabels}
          isApplying={isBulkApplying}
          onClearNotifications={handleBulkClearNotifications}
          onReject={handleBulkRejectClick}
          onDelete={handleBulkDeleteClick}
          onAssignTo={isInternal ? handleBulkAssignClick : undefined}
          onApplyBulkUpdates={handleBulkApplyUpdates}
        />
      )}

      {bulkActionError && (
        <div
          className="flex items-center justify-between px-4 py-2 border-b border-destructive/30 bg-destructive/10 text-destructive text-sm"
          role="alert"
        >
          <span>{bulkActionError}</span>
          {clearBulkActionError && (
            <button
              className="ml-2 text-destructive hover:text-destructive/80 font-medium"
              onClick={clearBulkActionError}
              aria-label="Dismiss error"
            >
              Dismiss
            </button>
          )}
        </div>
      )}

      <div className="flex items-center justify-between px-4 py-2 border-b border-border bg-muted/30">
        <div className="flex items-center gap-3 flex-wrap">
          {!loading && enquiries.length > 0 && (
            <label className="flex items-center gap-2 text-sm text-muted-foreground cursor-pointer select-none">
              <Checkbox
                checked={allSelected}
                onCheckedChange={handleSelectAllChange}
                aria-label="Select all enquiries"
              />
              Select all
            </label>
          )}
          {filterDrawerContent && (
            <button
              className="lg:hidden flex items-center gap-1 px-2 py-1 rounded-md text-sm text-muted-foreground hover:bg-accent/50 transition-colors"
              onClick={() => setIsFilterDrawerOpen(true)}
              aria-label="Open filters"
            >
              <SlidersHorizontal size={16} />
              Filters
              {activeFilterCount > 0 && (
                <span className="px-1.5 py-0.5 rounded-full bg-primary text-primary-foreground text-xs">
                  {activeFilterCount}
                </span>
              )}
            </button>
          )}
          <span className="text-sm text-muted-foreground" aria-live="polite">
            Showing {startItem}-{endItem} of {total} enquiries
            {filteredTotalHeads > 0 && `, ${filteredTotalHeads} guests`}
          </span>
          {activeFilterCount > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              {filters.status?.map((status) => (
                <button
                  key={status}
                  className="flex items-center gap-1 px-2 py-1 rounded-full bg-accent text-accent-foreground text-sm hover:bg-accent/80 transition-colors"
                  onClick={() => onStatusFilterClick(status)}
                  aria-label={`Remove ${STATUS_CONFIG[status].label} filter`}
                >
                  {STATUS_CONFIG[status].label}
                  <X size={14} />
                </button>
              ))}
              <button
                className="flex items-center gap-1 px-2 py-1 rounded-full bg-destructive/10 text-destructive text-sm hover:bg-destructive/20 transition-colors"
                onClick={onClearFilters}
                aria-label="Clear all filters"
              >
                Clear all
                <X size={14} />
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex-1 relative min-h-0">
        <div className="absolute inset-0 overflow-y-auto">
          {renderEnquiriesContent()}
        </div>
      </div>

      {!loading && enquiries.length > 0 && (
        <div className="flex items-center justify-between px-4 pt-3 pb-6 border-t border-border bg-card">
          <div className="flex items-center gap-2">
            <EDSButton
              variant="secondary"
              className="w-28"
              onClick={handlePrevPage}
              disabled={offset === 0}
            >
              <ChevronLeft size={16} />
              Previous
            </EDSButton>
            <span className="text-sm text-muted-foreground px-3">
              Page {Math.floor(offset / limit) + 1} of{" "}
              {Math.ceil(total / limit)}
            </span>
            <EDSButton
              variant="secondary"
              className="w-28"
              onClick={handleNextPage}
              disabled={offset + limit >= total}
            >
              Next
              <ChevronRight size={16} />
            </EDSButton>
          </div>
        </div>
      )}

      {filterDrawerContent && (
        <Sheet open={isFilterDrawerOpen} onOpenChange={setIsFilterDrawerOpen}>
          <SheetContent side="left" className="w-[320px] p-0 overflow-y-auto">
            <SheetHeader className="px-4 pt-4 pb-0">
              <SheetTitle>Filters</SheetTitle>
            </SheetHeader>
            {filterDrawerContent}
          </SheetContent>
        </Sheet>
      )}

      {isInternal && (
        <AssignToModal
          open={isBulkAssignModalOpen}
          users={users}
          currentAssignedTo={undefined}
          currentUserId={currentUserId}
          onSave={handleBulkAssignSave}
          onCancel={handleBulkAssignCancel}
        />
      )}

      <Dialog open={isRejectDialogOpen} onOpenChange={setIsRejectDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reject bookings</DialogTitle>
            <DialogDescription>
              Are you sure? Please confirm that you want to reject the selected
              bookings.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <EDSButton variant="secondary" onClick={handleBulkRejectCancel}>
              Cancel
            </EDSButton>
            <EDSButton
              variant="destructive"
              onClick={handleBulkRejectConfirm}
              disabled={isRejecting}
            >
              {isRejecting ? "Rejecting..." : "Reject"}
            </EDSButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete bookings</DialogTitle>
            <DialogDescription>
              Are you sure? Please confirm that you want to delete the selected
              bookings.
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            <label
              htmlFor="delete-reason"
              className="text-sm font-medium text-foreground mb-1 block"
            >
              Reason (optional)
            </label>
            <Select
              value={deleteReason}
              onValueChange={(value) =>
                setDeleteReason(value as BulkDeleteReason)
              }
            >
              <SelectTrigger id="delete-reason">
                <SelectValue placeholder="Select a reason" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="spam">Spam</SelectItem>
                <SelectItem value="duplicate">Duplicate</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <EDSButton variant="secondary" onClick={handleBulkDeleteCancel}>
              Cancel
            </EDSButton>
            <EDSButton
              variant="destructive"
              onClick={handleBulkDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </EDSButton>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
